/**
 * TypeScript types для MiniApp REST API (Stage 5.X.2+).
 *
 * Соответствуют Pydantic-схемам backend'а:
 *   - `apps/miniapp_api/schemas/auth.py`
 *   - `apps/miniapp_api/schemas/user.py`
 *   - `apps/miniapp_api/schemas/errors.py`
 *
 * Stage 5.X.9 добавит автогенерацию через `npx openapi-typescript` —
 * пока вручную (контракт стабилен в `docs/tg-miniapp.md` §8.1).
 */

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface LoginRequest {
  init_data: string;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface LogoutRequest {
  refresh_token?: string | null;
}

export interface UserPublicView {
  id: string; // UUID (public_id), не internal int
  telegram_user_id: number;
  first_name: string;
  last_name: string | null;
  username: string | null;
  avatar_url: string | null;
  language_preference: 'ru' | 'en';
  onboarding_completed_at: string | null; // ISO 8601 UTC
  created_at: string; // ISO 8601 UTC
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number; // секунды до истечения access_token (default 900)
  user: UserPublicView;
}

export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

// ---------------------------------------------------------------------------
// Error envelope (см. docs/tg-miniapp.md §8.0.1 + §8.8)
// ---------------------------------------------------------------------------

/**
 * Стабильный реестр error.code (UPPER_SNAKE_CASE).
 *
 * Полный список — `docs/tg-miniapp.md` §8.8. Здесь только те, которые
 * frontend Stage 5.X.2 различает поведенчески.
 */
export type ErrorCode =
  | 'AUTH_INVALID_INIT_DATA'
  | 'AUTH_STALE_INIT_DATA'
  | 'AUTH_USER_DELETED'
  | 'AUTH_INVALID_REFRESH_TOKEN'
  | 'AUTH_REFRESH_REUSE_DETECTED'
  | 'AUTH_TOKEN_BLACKLISTED'
  | 'AUTH_TOKEN_EXPIRED'
  | 'AUTH_INVALID_TOKEN'
  | 'VALIDATION_ERROR'
  | 'DOMAIN_ERROR'
  | 'INTERNAL_ERROR'
  // Catch-all для неизвестных кодов (forward-compat: backend может
  // добавить новые коды Stage 5.X.3+; UI рендерит generic-сообщение).
  | (string & Record<never, never>);

export interface ErrorPayload {
  code: ErrorCode;
  message: string;
  details?: Record<string, unknown> | null;
  trace_id?: string | null;
}

export interface ErrorEnvelope {
  error: ErrorPayload;
}

// ---------------------------------------------------------------------------
// ApiError — исключение для http-слоя
// ---------------------------------------------------------------------------

/**
 * Структурированная ошибка от backend'а MiniApp API.
 *
 * Бросается из `http()`-обёртки при 4xx/5xx ответе. Имеет typed `code` +
 * `status` для switch-based handling в роутере / use case'ах.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | null;
  readonly traceId: string | null;

  constructor(params: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: Record<string, unknown> | null;
    traceId?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? null;
    this.traceId = params.traceId ?? null;
  }

  /**
   * Type-guard: распознать ApiError через `instanceof` или duck-typing
   * (например, при пересечении WorkerSerialized boundary).
   */
  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}

/**
 * Network-уровневая ошибка (нет ответа от сервера, CORS, timeout).
 *
 * Отдельный класс чтобы UI мог различить "сервер вернул 500" (ApiError)
 * vs "не дошло до сервера" (NetworkError) — разное UX-сообщение.
 */
export class NetworkError extends Error {
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
